import React from 'react'
import {
  LogResponse,
  Carer,
  Home,
  Resident,
  LogCategoryItem,
} from '@lmc/ts-common'
import dayjs from 'dayjs'
import { Text, AvatarLabel, Heading } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { history } from '../../../../../store'
import LastUpdateBox from '../../../../../components/carePlans/LastUpdateBox'
import style from '../../LogScreen.module.less'
import LogRevisions from '../LogRevisions'
import LogMeasurements from '../LogMeasurements'
import PainChekAssessment from '../PainChekAssessment'
import GoalsList from 'screens/Residents/screens/ResidentsGoalsScreen/components/GoalsList/GoalsList'
import styled from 'styled-components'
import useGoalsIDs from 'screens/Residents/screens/ResidentsGoalsScreen/components/useGoals/useGoalsIDs'
import useGoalsAccess from 'components/AccessControl/features/useGoalsAccess'
import { isSeizureItemLog } from '../utils'
import GeoLocation from '../../../../../components/GeoLocation/GeoLocation'

// @ts-ignore
interface LogWithRevisions extends LogResponse {
  logRevisions?: LogResponse[]
  externalId?: string
  picture1?: string
  picture2?: string
  picture3?: string
  editedOn?: string
  reasonCode?: string
}

interface Props {
  log: LogWithRevisions
  resident: Resident
  item: LogCategoryItem
  loggedBy: Carer
  witnessedBy: Carer
  editedBy: Carer
  features: Home
  painChekAssessmentResult: any // TODO: import type from TS-Common
}

export const LogDetails = ({
  log,
  resident,
  item,
  loggedBy,
  witnessedBy,
  editedBy,
  features,
  painChekAssessmentResult,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const renderResidentAvatar = () => {
    if (!resident) return null
    const pic = resident.picture as unknown as string
    return (
      <div style={styles.avatarContainer} data-testid="resident-avatar">
        <AvatarLabel
          onSelect={() => history.push(`/residents/${resident?.id}/profile`)}
          label={`${resident.name.first} ${resident.name.last}`}
          picture={pic}
        />
      </div>
    )
  }

  const renderImages = () => {
    const images = [log.picture1, log.picture2, log.picture3]
    const { videoFile } = log

    const validImages = images.filter((url) => url)
    const imageUrls = validImages
      .filter((url) => url)
      .map((url, index) => (
        <img
          src={url}
          key={`image-${index + 1}`}
          data-testid={`image-${index + 1}`}
          className={style.image}
          alt=""
        />
      ))

    const shouldRender: boolean = Boolean(videoFile || validImages.length)

    if (!shouldRender) {
      return null
    }

    return (
      <div style={styles.mediaContainer}>
        <Text as="h4">{t('components.logEditForm.attachedMedia')}</Text>
        {Boolean(validImages.length) && (
          <div className={style.imagesContainer}>{imageUrls}</div>
        )}
        {Boolean(videoFile) && (
          <Video controls>
            <source src={videoFile} type="video/mp4" />
            <track
              src="captions_en.vtt"
              kind="captions"
              label="english_captions"
            />
          </Video>
        )}
      </div>
    )
  }

  const renderMeasurements = (renderLink?: boolean) => {
    if (item && (item.amountOneKey || item.amountTwoKey)) {
      return (
        <LogMeasurements
          log={log}
          item={item}
          onClick={(url: string) => history.push(url)}
          renderLink={renderLink}
        />
      )
    }

    return null
  }

  const hasGoals = useGoalsAccess()
  const { filteredGoals: goals } = useGoalsIDs({
    goalsIDs: log.goals as string[],
    residentID: resident?.id,
  })

  const showLinkOnly = isSeizureItemLog(item?.id)

  return (
    <>
      {renderResidentAvatar()}
      <div style={styles.descriptionContainer}>
        <Text as="p2" style={styles.title}>
          {t('components.logEditForm.description')}
        </Text>
        <br />
        <Text as="p2" style={styles.descriptionText}>
          {log.description}
        </Text>
        {witnessedBy && (
          <div style={styles.witnessContainer}>
            <Text as="p2" style={styles.title}>
              {t('components.logEditForm.witnessedBy', { name: '' })}
            </Text>
            <br />
            <Text as="p2" style={styles.descriptionText}>
              {witnessedBy.name.first} {witnessedBy.name.last}
            </Text>
          </div>
        )}
        {renderMeasurements(showLinkOnly)}
        {log.reasonCode ? (
          <div style={styles.outcome}>
            <Text as="p2" style={styles.title}>
              {'Outcome: '}
            </Text>
            <Text as="p2" style={styles.descriptionText}>
              {log.reasonCode}
            </Text>
          </div>
        ) : null}
      </div>
      <div style={styles.lastUpdateContainer}>
        <LastUpdateBox
          title={t('components.logEditForm.timeLogged')}
          carer={loggedBy}
          // @ts-ignore
          date={log.timeLogged}
          noDateText=""
        >
          {editedBy || witnessedBy ? (
            <div style={styles.metaText}>
              <Text as="p2">
                {witnessedBy &&
                  t('components.logEditForm.witnessedBy', {
                    name: `${witnessedBy.name.first} ${witnessedBy.name.last}`,
                  })}
                {editedBy &&
                  `• ${t('components.logEditForm.editedBy', {
                    name: `${editedBy.name.first} ${editedBy.name.last}`,
                    day: dayjs(log.editedOn).format('DD/MM/YYYY HH:MM'),
                  })}`}
              </Text>
            </div>
          ) : null}
        </LastUpdateBox>
      </div>
      {hasGoals && goals && (
        <GoalsListStyled
          residentID={resident?.id}
          items={goals}
          title={t('components.logEditForm.linkedGoals')}
        />
      )}
      {renderImages()}
      {painChekAssessmentResult && (
        <PainChekAssessment assessmentResult={painChekAssessmentResult} />
      )}
      {features.features?.geoLocation?.enabled && (
        <>
          <Heading level={4}>
            {t('components.logEditForm.locationAvailable')}
          </Heading>
          <GeolocationContainer>
            <GeoLocation
              longitude={log.longitude}
              latitude={log.latitude}
              accuracy={log.accuracy}
            />
          </GeolocationContainer>
        </>
      )}

      {log.logRevisions && log.logRevisions.length ? (
        <div className={style.logRevisions}>
          <Text as="p2">{t('components.logEditForm.logRevisions')}</Text>
          <LogRevisions log={log} />
        </div>
      ) : null}
    </>
  )
}

const GoalsListStyled = styled(GoalsList)`
  margin-top: 40px;
  margin-bottom: 80px;
`

const styles = {
  descriptionContainer: {
    background: 'white',
    borderRadius: 16,
    padding: '16px 24px 18px 24px',
  } as React.CSSProperties,
  descriptionText: {
    lineHeight: '24px',
    whiteSpace: 'pre-wrap',
  } as React.CSSProperties,
  title: { fontWeight: 'bold' },
  outcome: { paddingTop: 12 },
  lastUpdateContainer: { paddingTop: 12, paddingBottom: 32 },
  mediaContainer: { paddingBottom: 48 },
  avatarContainer: { paddingTop: 12, paddingBottom: 24, fontWeight: 'bold' },
  metaText: { paddingTop: 8, paddingBottom: 8 },
  witnessContainer: { paddingTop: 12 },
}

const Video = styled.video`
  width: 320px;
  height: 180px;
`

const GeolocationContainer = styled.div`
  margin-top: 12px;
`

export default LogDetails
